import React, { useState, useEffect } from 'react';
import { getClients, createClient, updateClient, getZones, getCommerciaux, getSegments, uploadClientPhoto, uploadClientPhotoBoutique } from '../services/api';
import { FiPlus, FiEdit2, FiSearch, FiMapPin, FiEye, FiFilter, FiImage, FiUpload, FiX, FiCamera, FiHome } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from '../components/SearchableSelect';

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isDev ? 'http://localhost:5055' : '';
const POTENTIELS = ['faible', 'moyen', 'eleve', 'tres_eleve'];
const POTENTIEL_LABELS: Record<string, string> = { faible: 'Faible', moyen: 'Moyen', eleve: 'Eleve', tres_eleve: 'Tres eleve' };

const Clients: React.FC = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filterZone, setFilterZone] = useState('');
    const [filterPotentiel, setFilterPotentiel] = useState('');
    const [filterCommercial, setFilterCommercial] = useState('');
    const [filterSegment, setFilterSegment] = useState('');
    const [filterActif, setFilterActif] = useState('');
    const [showFilters] = useState(false); // kept for compatibility
    const [zones, setZones] = useState<any[]>([]);
    const [commerciaux, setCommerciaux] = useState<any[]>([]);
    const [segments, setSegments] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editClient, setEditClient] = useState<any>(null);
    const [form, setForm] = useState<any>({ raison_sociale: '', nom_contact: '', prenom_contact: '', email: '', telephone: '', adresse: '', ville: '', latitude: '', longitude: '', segment_id: '', zone_id: '', potentiel: 'moyen', commercial_id: '', notes: '' });

    const loadClients = () => {
        const params: any = { page, limit: 20 };
        if (search) params.search = search;
        if (filterZone) params.zone_id = filterZone;
        if (filterPotentiel) params.potentiel = filterPotentiel;
        if (filterCommercial) params.commercial_id = filterCommercial;
        if (filterSegment) params.segment_id = filterSegment;
        getClients(params).then(res => {
            setClients(res.data.data); setTotal(res.data.total);
        });
    };

    useEffect(() => {
        Promise.all([getZones(), getCommerciaux(), getSegments()]).then(([zRes, cRes, sRes]) => {
            setZones(zRes.data);
            setCommerciaux(cRes.data);
            setSegments(sRes.data);
        });
    }, []);

    useEffect(() => { loadClients(); }, [page, search, filterZone, filterPotentiel, filterCommercial, filterSegment, filterActif]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = { ...form, latitude: form.latitude || null, longitude: form.longitude || null, segment_id: form.segment_id || null, zone_id: form.zone_id || null, commercial_id: form.commercial_id || null };
            if (editClient) await updateClient(editClient.id, data);
            else await createClient(data);
            setShowModal(false); setEditClient(null); loadClients();
        } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    };

    const openEdit = (c: any) => {
        setEditClient(c);
        setForm({ raison_sociale: c.raison_sociale, nom_contact: c.nom_contact || '', prenom_contact: c.prenom_contact || '', email: c.email || '', telephone: c.telephone || '', adresse: c.adresse || '', ville: c.ville || '', latitude: c.latitude || '', longitude: c.longitude || '', segment_id: c.segment_id || '', zone_id: c.zone_id || '', potentiel: c.potentiel || 'moyen', commercial_id: c.commercial_id || '', notes: c.notes || '' });
        setShowModal(true);
    };

    const potentielColor = (p: string) => {
        const colors: any = { faible: '#94A3B8', moyen: '#F59E0B', eleve: '#16A34A', tres_eleve: '#3B82F6' };
        return colors[p] || '#999';
    };

    const activeFilterCount = [filterZone, filterPotentiel, filterCommercial, filterSegment, filterActif].filter(Boolean).length;

    const clearFilters = () => {
        setFilterZone(''); setFilterPotentiel(''); setFilterCommercial(''); setFilterSegment(''); setFilterActif(''); setPage(1);
    };

    const handleImageUpload = async (clientId: number, file: File, type: 'photo' | 'boutique' = 'photo') => {
        try {
            if (type === 'boutique') await uploadClientPhotoBoutique(clientId, file);
            else await uploadClientPhoto(clientId, file);
            loadClients();
        } catch { /* ignore */ }
    };

    return (
        <div className="page-clients">
            <div className="page-header">
                <h2>Gestion des Clients</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    <span className="badge" style={{ background: '#E2E8F0', color: '#334155', fontSize: 13, padding: '6px 12px' }}>{total} clients</span>
                    <button className="btn btn-primary" onClick={() => { setEditClient(null); setForm({ raison_sociale: '', nom_contact: '', prenom_contact: '', email: '', telephone: '', adresse: '', ville: '', latitude: '', longitude: '', segment_id: '', zone_id: '', potentiel: 'moyen', commercial_id: '', notes: '' }); setShowModal(true); }}>
                        <FiPlus /> Nouveau client
                    </button>
                </div>
            </div>

            <div className="filters-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
                    <FiSearch />
                    <input placeholder="Rechercher par nom, contact, telephone..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <select value={filterZone} onChange={e => { setFilterZone(e.target.value); setPage(1); }}
                    style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: filterZone ? '#DCFCE7' : '#fff', color: filterZone ? '#16A34A' : '#475569', fontWeight: filterZone ? 600 : 400 }}>
                    <option value="">Toutes les zones</option>
                    {zones.map((z: any) => <option key={z.id} value={z.id}>{z.nom}</option>)}
                </select>
                <select value={filterPotentiel} onChange={e => { setFilterPotentiel(e.target.value); setPage(1); }}
                    style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: filterPotentiel ? '#DCFCE7' : '#fff', color: filterPotentiel ? '#16A34A' : '#475569', fontWeight: filterPotentiel ? 600 : 400 }}>
                    <option value="">Tous potentiels</option>
                    {POTENTIELS.map(p => <option key={p} value={p}>{POTENTIEL_LABELS[p]}</option>)}
                </select>
                <select value={filterSegment} onChange={e => { setFilterSegment(e.target.value); setPage(1); }}
                    style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: filterSegment ? '#DCFCE7' : '#fff', color: filterSegment ? '#16A34A' : '#475569', fontWeight: filterSegment ? 600 : 400 }}>
                    <option value="">Tous segments</option>
                    {segments.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
                <div style={{ minWidth: 160 }}>
                    <SearchableSelect
                        value={filterCommercial}
                        onChange={(v: string) => { setFilterCommercial(v); setPage(1); }}
                        placeholder="Tous commerciaux"
                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}` }))}
                    />
                </div>
                {activeFilterCount > 0 && (
                    <button className="btn btn-secondary" onClick={clearFilters} style={{ fontSize: 12, padding: '6px 10px' }}>
                        <FiX size={14} /> Effacer ({activeFilterCount})
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th style={{ width: 95 }}>Photos</th>
                            <th>Client</th>
                            <th>Contact</th>
                            <th>Zone</th>
                            <th>Segment</th>
                            <th>Potentiel</th>
                            <th>Commercial</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map(c => (
                            <tr key={c.id}>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {/* Photo client */}
                                        <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
                                            {c.image ? (
                                                <img src={`${API_URL}${c.image}`} alt="" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', border: '1.5px solid #E2E8F0' }} />
                                            ) : (
                                                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FiImage size={14} color="#94A3B8" />
                                                </div>
                                            )}
                                            <label style={{ position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: '#fff', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                                <FiCamera size={8} color="#64748B" />
                                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleImageUpload(c.id, e.target.files[0], 'photo'); }} />
                                            </label>
                                        </div>
                                        {/* Photo boutique */}
                                        <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
                                            {c.photo_boutique ? (
                                                <img src={`${API_URL}${c.photo_boutique}`} alt="" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', border: '1.5px solid #E2E8F0' }} />
                                            ) : (
                                                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FiHome size={14} color="#F59E0B" />
                                                </div>
                                            )}
                                            <label style={{ position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: '#fff', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                                <FiCamera size={8} color="#F59E0B" />
                                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleImageUpload(c.id, e.target.files[0], 'boutique'); }} />
                                            </label>
                                        </div>
                                    </div>
                                </td>
                                <td><strong>{c.raison_sociale}</strong><br /><small style={{ color: '#94A3B8' }}>{c.ville || ''}</small></td>
                                <td>{c.telephone || c.email || '-'}</td>
                                <td>{c.zone_nom || '-'}</td>
                                <td>{c.segment_nom ? <span className="badge" style={{ backgroundColor: c.segment_couleur || '#E2E8F0', color: '#fff' }}>{c.segment_nom}</span> : '-'}</td>
                                <td><span className="badge" style={{ backgroundColor: potentielColor(c.potentiel) }}>{POTENTIEL_LABELS[c.potentiel] || c.potentiel}</span></td>
                                <td>{c.commercial_nom || '-'}</td>
                                <td className="actions">
                                    <button className="btn-icon" onClick={() => navigate(`/admin/clients/${c.id}`)} title="Voir"><FiEye /></button>
                                    <button className="btn-icon" onClick={() => openEdit(c)} title="Modifier"><FiEdit2 /></button>
                                    {c.latitude && <button className="btn-icon" title="Localiser"><FiMapPin /></button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Precedent</button>
                <span>Page {page} / {Math.ceil(total / 20) || 1}</span>
                <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Suivant</button>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
                        <h3>{editClient ? 'Modifier' : 'Nouveau'} Client</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group"><label>Raison sociale *</label><input value={form.raison_sociale} onChange={e => setForm({ ...form, raison_sociale: e.target.value })} required /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Nom contact</label><input value={form.nom_contact} onChange={e => setForm({ ...form, nom_contact: e.target.value })} /></div>
                                <div className="form-group"><label>Prenom contact</label><input value={form.prenom_contact} onChange={e => setForm({ ...form, prenom_contact: e.target.value })} /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                                <div className="form-group"><label>Telephone</label><input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} /></div>
                            </div>
                            <div className="form-group"><label>Adresse</label><input value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Ville</label><input value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} /></div>
                                <div className="form-group">
                                    <label>Potentiel</label>
                                    <select value={form.potentiel} onChange={e => setForm({ ...form, potentiel: e.target.value })}>
                                        {POTENTIELS.map(p => <option key={p} value={p}>{POTENTIEL_LABELS[p]}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Segment</label>
                                    <select value={form.segment_id} onChange={e => setForm({ ...form, segment_id: e.target.value })}><option value="">--</option>{segments.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}</select>
                                </div>
                                <div className="form-group">
                                    <label>Zone</label>
                                    <select value={form.zone_id} onChange={e => setForm({ ...form, zone_id: e.target.value })}><option value="">--</option>{zones.map((z: any) => <option key={z.id} value={z.id}>{z.nom}</option>)}</select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Commercial</label>
                                    <SearchableSelect
                                        value={form.commercial_id}
                                        onChange={(v: string) => setForm({ ...form, commercial_id: v })}
                                        placeholder="--"
                                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}` }))}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Latitude</label><input type="number" step="any" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} /></div>
                                <div className="form-group"><label>Longitude</label><input type="number" step="any" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} /></div>
                            </div>
                            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editClient ? 'Mettre a jour' : 'Creer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
